/**
 * A router link that starts its page's code and first reads on intent: a pointer resting on it, focus, or the
 * start of a tap (`lib/route-prefetch.ts`). Otherwise exactly `Link`; handlers passed in still run.
 */
import { forwardRef, type ComponentProps } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePrefetchOnIntent } from "@/lib/route-prefetch";

type Props = ComponentProps<typeof Link> & {
  /** The concept's storyboard, when the row already names it: its read goes out without waiting for the brief. */
  storyboardId?: number | null;
};

export const PrefetchLink = forwardRef<HTMLAnchorElement, Props>(function PrefetchLink(
  { storyboardId, onPointerEnter, onPointerLeave, onFocus, onTouchStart, ...props },
  ref,
) {
  const queryClient = useQueryClient();
  const href = typeof props.to === "string" ? props.to : `${props.to.pathname ?? ""}${props.to.search ?? ""}`;
  const intent = usePrefetchOnIntent(queryClient, href, { storyboardId });
  return (
    <Link
      ref={ref}
      {...props}
      onPointerEnter={(event) => {
        onPointerEnter?.(event);
        intent.onPointerEnter(event);
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event);
        intent.onPointerLeave();
      }}
      onFocus={(event) => {
        onFocus?.(event);
        intent.onFocus();
      }}
      onTouchStart={(event) => {
        onTouchStart?.(event);
        intent.onTouchStart();
      }}
    />
  );
});
